import { Layout, Sidebar, Box, Star } from 'lucide-react';

interface Template {
  id?: string;
  label: string;
  description?: string;
  slots: string[];
  layout: {
    hasSidebar: boolean;
  };
  recommended: string[];
}

interface Slot {
  label: string;
  description: string;
  position: string;
  allowsMultiple: boolean;
}

interface TemplatePreviewProps {
  template: Template;
  availableSlots: Record<string, Slot>;
}

export function TemplatePreview({ template, availableSlots }: TemplatePreviewProps) {
  // Group slots by position
  const slotsByPosition: Record<string, Array<{ id: string; slot: Slot }>> = {};

  template.slots.forEach((slotId) => {
    const slot = availableSlots[slotId];
    if (!slot) return;

    const position = slot.position || 'other';
    if (!slotsByPosition[position]) {
      slotsByPosition[position] = [];
    }
    slotsByPosition[position].push({ id: slotId, slot });
  });

  // Position order for layout
  const positionOrder = ['top', 'center', 'bottom', 'sidebar', 'other'];
  const orderedPositions = positionOrder.filter((pos) => slotsByPosition[pos]);

  return (
    <div className="template-preview">
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
          <Layout className="w-5 h-5 text-primary-600" />
          <span>Anteprima Template</span>
        </h3>
        <p className="text-sm text-gray-600 mt-1">{template.label}</p>
      </div>

      {/* Layout Info */}
      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center space-x-2 text-sm">
          {template.layout.hasSidebar ? (
            <>
              <Sidebar className="w-4 h-4 text-blue-600" />
              <span className="text-blue-800 font-medium">Layout con sidebar</span>
            </>
          ) : (
            <>
              <Layout className="w-4 h-4 text-blue-600" />
              <span className="text-blue-800 font-medium">Layout full-width</span>
            </>
          )}
        </div>
      </div>

      {/* Slots Visualization */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-700 uppercase">
          Struttura Slots ({template.slots.length})
        </h4>

        {template.slots.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
            <Box className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Nessuno slot selezionato</p>
          </div>
        ) : (
          <div className="space-y-3">
            {orderedPositions.map((position) => (
              <div key={position} className="space-y-2">
                {/* Position Label */}
                <div className="flex items-center space-x-2">
                  <div className="h-px bg-gray-300 flex-1"></div>
                  <span className="text-xs font-medium text-gray-500 uppercase px-2">
                    {position}
                  </span>
                  <div className="h-px bg-gray-300 flex-1"></div>
                </div>

                {/* Slots in this position */}
                <div className="space-y-2">
                  {slotsByPosition[position].map(({ id, slot }) => (
                    <div
                      key={id}
                      className="border-2 border-gray-300 rounded-lg p-3 bg-white hover:border-primary-400 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-gray-900">{slot.label}</span>
                            {slot.allowsMultiple && (
                              <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                                multiple
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 font-mono mt-0.5">{id}</p>
                          <p className="text-sm text-gray-600 mt-1">{slot.description}</p>
                        </div>
                        <Box className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recommended Sections */}
      {template.recommended && template.recommended.length > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 uppercase mb-3 flex items-center space-x-2">
            <Star className="w-4 h-4 text-yellow-500" />
            <span>Sezioni Raccomandate ({template.recommended.length})</span>
          </h4>
          <div className="flex flex-wrap gap-2">
            {template.recommended.map((sectionId) => (
              <span
                key={sectionId}
                className="inline-flex items-center px-3 py-1.5 bg-green-100 text-green-700 text-sm rounded-lg border border-green-200"
              >
                {sectionId}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Metadata */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Totale Slots</span>
            <p className="font-medium text-gray-900">{template.slots.length}</p>
          </div>
          <div>
            <span className="text-gray-500">Sidebar</span>
            <p className="font-medium text-gray-900">
              {template.layout.hasSidebar ? 'Sì' : 'No'}
            </p>
          </div>
          <div>
            <span className="text-gray-500">Posizioni</span>
            <p className="font-medium text-gray-900">{orderedPositions.length}</p>
          </div>
          <div>
            <span className="text-gray-500">Raccomandati</span>
            <p className="font-medium text-gray-900">{template.recommended?.length || 0}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
